import { useEffect, useState, useRef } from 'react';
import {
  ChatContainer,
  Container,
  Form,
  TextArea,
  MessageBubble,
  MessageRow,
  SubmitButton,
  Wrapper,
  ErrorComponent
} from './styles';
import { Select } from '../../pages/Admin/styles';

// -----------------------
//     Type Definitions
// -----------------------
export interface Message {
  sender: 'user' | 'bot';
  text: string;
}

export interface ChatMessengerProps {
  userId?: string | null;
}

const openaiModels = [
  // GPT-4 Series
  'gpt-4',
  'gpt-4-turbo',
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'gpt-4.5',

  // GPT-3.5 Series
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-16k',
  'gpt-3.5-turbo-instruct',

  // InstructGPT Models
  'text-davinci-003',
  'text-davinci-002',
  'text-curie-001',
  'text-babbage-001',
  'text-ada-001',

  // Code Models
  'code-davinci-002',
  'code-cushman-001',

  // Reasoning Models (o-series)
  'o1',
  'o1-mini',
  'o1-pro',
  'o3',
  'o3-mini',
  'o3-mini-high',
  'o4-mini',
  'o4-mini-high',

  // Embedding Models
  'text-embedding-ada-002',

  // Audio Model
  'whisper-1',

  // Image Models
  'dall-e-2',
  'dall-e-3'
];

const BACKEND_URL = 'https://internal.guiderbackend.com';
// const BACKEND_URL = 'http://localhost:3001';

const defaultPrompt = `You are SheikhGPT, a multilingual Islamic bot designed to answer Islam-related questions with accuracy and depth. Fluent in Arabic, Bahasa, Bosnian, French, Turkish, Urdu, and more, you craft precise, evidence-based responses exclusively from the Sunni tradition. 
        
        This is the history of our conversation so far, you are "Assistant":

        {history}

        Now here's my Question: {question}

        Answer the question based on the following context and the history of our conversation:
        {context}
        `;

const generateRandomCode = () => {
  return Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
};

/**
 * ChatMessenger Component (TypeScript version)
 *
 * Receives messages as a prop. Calls back to `onSend` and optionally `onBotReply`.
 */
export default function ChatMessenger({ userId = null }: ChatMessengerProps) {
  // Chat messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionCode] = useState(() => generateRandomCode());

  // Form states
  const [systemPrompt, setSystemPrompt] = useState<string>(() => {
    return localStorage.getItem('systemPrompt') || defaultPrompt;
  });

  const [question, setQuestion] = useState<string>('What does the text say?');
  const [llmName, setLlmName] = useState<'Anthropic' | 'OpenAI'>('OpenAI');
  const [temperature, setTemperature] = useState<number>(0.7);
  const [selectedOpenAIModel, setSelectedOpenAIModel] = useState<string>('o4-mini');

  // Add a new state to track the checkbox
  const [isTemperatureEnabled, setIsTemperatureEnabled] = useState<boolean>(true);

  // Create a reference for the chat container
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('systemPrompt', systemPrompt);
  }, [systemPrompt]);

  useEffect(() => {
    if (llmName !== 'OpenAI') {
      setSelectedOpenAIModel('o4-mini');
    }
  }, [llmName]);

  useEffect(() => {
    // Scroll to the bottom of the chat container when messages change
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Any error from the server
  const [error, setError] = useState<string>('');
  const [isIngesting, setIsIngesting] = useState<boolean>(false);

  const handleIngest = async () => {
    if (isIngesting) return;
    setIsIngesting(true);
    const response = await fetch(`${BACKEND_URL}/ingest`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    console.log(data);
    setIsIngesting(false);
    alert(
      'Ingestion in progress. Please wait for about 10 minutes before sending your question. Here are the files ingested: ' +
        JSON.stringify(data)
    );
  };

  const handleReset = async () => {
    setSystemPrompt(defaultPrompt);
  };

  /**
   * Send the user's question to the server (POST request).
   * The body format:
   * {
   *   "conversationId": "1234",
   *   "question": "...",
   *   "systemPrompt": "...",
   *   "llmName": "Anthropic" (or "OpenAI")
   * }
   */
  const handleSend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // 1) Display user question in chat
    if (question.trim() === '') return;
    setMessages((prev) => [...prev, { sender: 'user', text: question.trim() }]);

    try {
      console.log(`Sending via ${userId ?? '1234'}-${sessionCode}`);
      // 2) Make the POST request
      const response = await fetch(`${BACKEND_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: `${userId ?? '1234'}-${sessionCode}`,
          question: question.trim(),
          systemPrompt: systemPrompt.trim(),
          llmName: llmName,
          llmOptions: {
            temperature: isTemperatureEnabled ? temperature : undefined,
            modelName: llmName === 'OpenAI' ? selectedOpenAIModel : undefined
          }
        })
      });
      console.log('response:', response);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          `HTTP error! status: ${response.status} ${response.statusText}: ${error.message}`
        );
      }

      // 3) Assume server returns JSON with the property "answer" or similar
      const data = await response.json();

      // For this demo, let's assume the response has { answer: string }
      const botReply = data.answer || JSON.stringify(data);

      // 4) Display bot reply in chat
      setMessages((prev) => [...prev, { sender: 'bot', text: botReply }]);
    } catch (err: any) {
      console.error('Error during POST:', err);
      setError(err.message || 'An error occurred while sending your question.');
      alert(err.message || 'An error occurred while sending your question.');
    }
  };

  const ingestButtonText = isIngesting ? 'Ingesting... Please wait..' : 'Ingest';
  const resetButtonText = 'Reset Prompt';
  return (
    <Wrapper style={{ display: 'flex' }}>
      <Container style={{ flex: 1, paddingRight: '10px' }}>
        <Form onSubmit={handleSend}>
          {error && <ErrorComponent>{error}</ErrorComponent>}

          <label htmlFor="systemPrompt" style={{ textAlign: 'left' }}>
            System Prompt:
          </label>
          <TextArea
            id="systemPrompt"
            rows={3}
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
          />

          <label htmlFor="question" style={{ textAlign: 'left' }}>
            Question:
          </label>
          <TextArea
            id="question"
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <label htmlFor="llmName" style={{ textAlign: 'left' }}>
            Choose LLM:
          </label>
          <Select
            id="llmName"
            value={llmName}
            onChange={(e) => setLlmName(e.target.value as 'Anthropic' | 'OpenAI')}
          >
            <option value="OpenAI">OpenAI</option>
            <option value="Anthropic">Anthropic</option>
          </Select>

          {llmName === 'OpenAI' && (
            <div>
              <label htmlFor="openaiModel" style={{ textAlign: 'left' }}>
                Choose OpenAI Model:
              </label>
              <Select
                id="openaiModel"
                value={selectedOpenAIModel}
                onChange={(e) => setSelectedOpenAIModel(e.target.value)}
              >
                {openaiModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <label htmlFor="temperature" style={{ textAlign: 'left' }}>
            Temperature:
          </label>
          <input
            type="range"
            id="temperature"
            min="0"
            max="1"
            step="0.1"
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            style={{ width: '150px', margin: '10px 0' }}
          />
          <span>{temperature}</span>

          <label htmlFor="enableTemperature" style={{ textAlign: 'left' }}>
            Enable Temperature:
          </label>
          <input
            type="checkbox"
            id="enableTemperature"
            checked={isTemperatureEnabled}
            onChange={(e) => setIsTemperatureEnabled(e.target.checked)}
            style={{ margin: '10px 0' }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px' }}>
            <SubmitButton type="submit">Send</SubmitButton>
            <SubmitButton onClick={handleIngest}>{ingestButtonText}</SubmitButton>
            <SubmitButton onClick={handleReset}>{resetButtonText}</SubmitButton>
          </div>
        </Form>
      </Container>
      <Container style={{ flex: 2 }}>
        <ChatContainer ref={chatContainerRef} style={{ height: '500px', overflowY: 'auto' }}>
          {messages.map((msg, index) => (
            <MessageRow key={index} isUser={msg.sender === 'user'}>
              <MessageBubble isUser={msg.sender === 'user'}>
                {msg.text.split('\\n').map((line, lineIndex) => (
                  <span key={lineIndex}>
                    {line}
                    {lineIndex < msg.text.split('\\n').length - 1 && <br />}
                  </span>
                ))}
              </MessageBubble>
            </MessageRow>
          ))}
        </ChatContainer>
      </Container>
    </Wrapper>
  );
}
